import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { adminService, ShoppingProduct } from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";

const ManageStockIndia = () => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShoppingProduct | null>(
    null
  );
  const [products, setProducts] = useState<ShoppingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  const [formData, setFormData] = useState({
    item_id: "",
    sr_no: "",
    age: "",
    book_title: "",
    description: "",
    price: 0,
    base_cost: 0,
    additional_cost: 0,
    count: 0,
    country: "IND",
  });

  const fetchProducts = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const data = await adminService.getShoppingProducts({
          country: "IND",
          ordering: "id",
          page: page,
          page_size: pagination.pageSize,
        });
        setProducts(data.results);
        setPagination(prev => ({
          ...prev,
          count: data.count,
          page: page,
        }));
      } catch (error: any) {
        console.error("Error fetching products:", error);
        showErrorToast(error, "Failed to load products. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ["price", "count", "base_cost", "additional_cost"].includes(name)
        ? Number(value)
        : value,
    }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      age: value,
    }));
  };

  const handleSave = async () => {
    try {
      await adminService.createShoppingProduct({
        ...formData,
        description: formData.book_title,
        country: "IND",
      });
      toast.success("Product added successfully");
      setAddDialogOpen(false);
      setFormData({
        item_id: "",
        sr_no: "",
        age: "",
        book_title: "",
        description: "",
        price: 0,
        base_cost: 0,
        additional_cost: 0,
        count: 0,
        country: "IND",
      });
      fetchProducts(pagination.page);
    } catch (error: any) {
      console.error("Error saving product:", error);
      showErrorToast(
        error,
        "Failed to save product. Please check if Product Code already exists."
      );
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      await adminService.updateShoppingProduct(selectedItem.id, {
        ...formData,
        description: formData.book_title,
      });
      toast.success("Product updated successfully");
      setEditDialogOpen(false);
      setSelectedItem(null);
      fetchProducts(pagination.page);
    } catch (error: any) {
      console.error("Error updating product:", error);
      showErrorToast(error, "Failed to update product");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }
    try {
      await adminService.deleteShoppingProduct(id);
      toast.success("Product deleted successfully");
      fetchProducts(pagination.page);
    } catch (error: any) {
      console.error("Error deleting product:", error);
      showErrorToast(error, "Failed to delete product");
    }
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);

  return (
    <AdminLayout>
      <div className="min-w-0 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <h2 className="text-balance text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
              Manage stock India
            </h2>
          </div>
          <div className="flex w-full shrink-0 sm:w-auto sm:justify-end">
            <Button
              className="w-full bg-[#654321] text-white hover:bg-[#543210] sm:w-auto"
              onClick={() => {
                setFormData({
                  item_id: "",
                  sr_no: "",
                  age: "",
                  book_title: "",
                  description: "",
                  price: 0,
                  base_cost: 0,
                  additional_cost: 0,
                  count: 0,
                  country: "IND",
                });
                setAddDialogOpen(true);
              }}
            >
              Add New Product
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[52rem] table-auto text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    ID
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Code
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Age
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Name
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Price
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Base Cost
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Add. Cost %
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Availability
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Country
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-8 text-center text-muted-foreground sm:px-6"
                    >
                      Loading products...
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-8 text-center text-muted-foreground sm:px-6"
                    >
                      No products found
                    </td>
                  </tr>
                ) : (
                  products.map(item => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="px-3 py-3 text-sm sm:px-5">{item.id}</td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.item_id}
                      </td>
                      <td className="px-3 py-3 text-sm sm:px-5">{item.age}</td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.book_title ?? item.description}
                      </td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.price}
                      </td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.base_cost ?? "-"}
                      </td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.additional_cost ?? "-"}
                      </td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.count}
                      </td>
                      <td className="px-3 py-3 text-sm sm:px-5">
                        {item.country}
                      </td>
                      <td className="px-3 py-3 sm:px-5">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="h-8 w-8 rounded-full bg-primary p-0 text-primary-foreground hover:bg-primary/90"
                            onClick={() => {
                              setSelectedItem(item);
                              setFormData({
                                item_id: item.item_id,
                                sr_no: item.sr_no ?? "",
                                age: item.age,
                                book_title:
                                  item.book_title ?? item.description ?? "",
                                description: item.description ?? "",
                                price: Number(item.price),
                                base_cost: Number(item.base_cost ?? 0),
                                additional_cost: Number(
                                  item.additional_cost ?? 0
                                ),
                                count: Number(item.count),
                                country: item.country,
                              });
                              setEditDialogOpen(true);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 w-8 rounded-full bg-destructive p-0 text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination and Count Summary */}
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-sm text-muted-foreground">
            {pagination.count > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-foreground">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">
                  {pagination.count}
                </span>{" "}
                results
              </>
            ) : (
              "No results to show"
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchProducts(1)}
              disabled={pagination.page === 1 || loading}
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchProducts(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
            >
              ‹ Back
            </Button>
            <div className="flex items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
              Page {pagination.page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchProducts(pagination.page + 1)}
              disabled={pagination.page >= totalPages || loading}
            >
              Next ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchProducts(totalPages)}
              disabled={pagination.page >= totalPages || loading}
            >
              Last »
            </Button>
          </div>
        </div>
      </div>

      {/* Add Product Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label required>Product Code</Label>
              <Input
                name="item_id"
                value={formData.item_id}
                onChange={handleInputChange}
                placeholder="Enter code"
              />
            </div>

            <div>
              <Label required>Age Group</Label>
              <Select value={formData.age} onValueChange={handleSelectChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select age" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5to9">5 to 9</SelectItem>
                  <SelectItem value="10to12">10 to 12</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label required>Name of Book</Label>
              <Input
                name="book_title"
                value={formData.book_title}
                onChange={handleInputChange}
                placeholder="Enter book name"
              />
            </div>
            <div>
              <Label required>Price</Label>
              <Input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="Enter price"
              />
            </div>
            <div>
              <Label>Base Cost</Label>
              <Input
                type="number"
                name="base_cost"
                value={formData.base_cost}
                onChange={handleInputChange}
                placeholder="Base shipping cost"
              />
            </div>
            <div>
              <Label>Additional Cost %</Label>
              <Input
                type="number"
                name="additional_cost"
                value={formData.additional_cost}
                onChange={handleInputChange}
                placeholder="Additional cost %"
              />
            </div>
            <div>
              <Label required>Availability</Label>
              <Input
                type="number"
                name="count"
                value={formData.count}
                onChange={handleInputChange}
                placeholder="Enter quantity"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input value="IND" readOnly />
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleSave}
            >
              Save
            </Button>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label required>Product Code</Label>
              <Input
                name="item_id"
                value={formData.item_id}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <Label required>Age Group</Label>
              <Input
                name="age"
                value={formData.age}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label required>Name of Book</Label>
              <Input
                name="book_title"
                value={formData.book_title}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label required>Price</Label>
              <Input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label>Base Cost</Label>
              <Input
                type="number"
                name="base_cost"
                value={formData.base_cost}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label>Additional Cost %</Label>
              <Input
                type="number"
                name="additional_cost"
                value={formData.additional_cost}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label required>Availability</Label>
              <Input
                type="number"
                name="count"
                value={formData.count}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={formData.country} readOnly />
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleUpdate}
            >
              Update
            </Button>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ManageStockIndia;
